import { AxiosError } from 'axios'
import { FileSignature } from 'lucide-react'
import ListHeader from '@/common/components/ListHeader'
import { Mapper } from '@/common/services/mapper'
import { useMaintenanceManualEntryProposals } from '../../hooks/useMaintenanceManualEntryProposals'
import { MaintenanceManualEntryProposalsTransformer } from '../../services/maintenanceManualEntryProposalsTransformer'
import { ManualEntryProposalCardItem } from '../../types/maintenanceManualEntryProposal.type'
import ProposalKanbanColumn from '../../components/ProposalKanbanColumn'
import ProposalCard from '../../components/ProposalCard'

const isForbidden = (error: unknown) => (error as AxiosError | null)?.response?.status === 403

const MaintenanceProposalsList = () => {
  const awaitingYou = useMaintenanceManualEntryProposals<ManualEntryProposalCardItem[]>({
    filters: { status: 'awaiting_establishment_response' },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceManualEntryProposalsTransformer.cardsTransformer('awaiting_establishment_response'),
    }),
  })

  const awaitingClient = useMaintenanceManualEntryProposals<ManualEntryProposalCardItem[]>({
    filters: { status: 'awaiting_client_response' },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceManualEntryProposalsTransformer.cardsTransformer('awaiting_client_response'),
    }),
  })

  const accepted = useMaintenanceManualEntryProposals<ManualEntryProposalCardItem[]>({
    filters: { status: 'completed' },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceManualEntryProposalsTransformer.cardsTransformer('completed'),
    }),
  })

  const canceled = useMaintenanceManualEntryProposals<ManualEntryProposalCardItem[]>({
    filters: { status: 'canceled' },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceManualEntryProposalsTransformer.cardsTransformer('canceled'),
    }),
  })

  const finalized = [...accepted.data, ...canceled.data]

  return (
    <div className="inner-screen gap-6 overflow-hidden">
      <ListHeader
        Icon={<FileSignature className="w-10 h-10" strokeWidth={1.5} />}
        title="Propostas de manutenção"
      />

      <div className="flex gap-4 flex-1 overflow-x-auto overflow-y-hidden">
        <ProposalKanbanColumn
          title="Aguardando sua resposta"
          count={awaitingYou.data.length}
          accentClassName="bg-yellow-400"
          isLoading={awaitingYou.isLoading}
          isError={awaitingYou.isError}
          forbidden={isForbidden(awaitingYou.error)}
        >
          {awaitingYou.data.map((proposal) => (
            <ProposalCard key={proposal.id} data={proposal} accentClassName="bg-yellow-400" />
          ))}
        </ProposalKanbanColumn>

        <ProposalKanbanColumn
          title="Aguardando resposta do cliente"
          count={awaitingClient.data.length}
          accentClassName="bg-primary-blue"
          isLoading={awaitingClient.isLoading}
          isError={awaitingClient.isError}
          forbidden={isForbidden(awaitingClient.error)}
        >
          {awaitingClient.data.map((proposal) => (
            <ProposalCard key={proposal.id} data={proposal} accentClassName="bg-primary-blue" />
          ))}
        </ProposalKanbanColumn>

        <ProposalKanbanColumn
          title="Finalizadas"
          count={finalized.length}
          isLoading={accepted.isLoading || canceled.isLoading}
          isError={accepted.isError || canceled.isError}
          forbidden={isForbidden(accepted.error) || isForbidden(canceled.error)}
        >
          {finalized.map((proposal) => (
            <ProposalCard key={proposal.id} data={proposal} />
          ))}
        </ProposalKanbanColumn>
      </div>
    </div>
  )
}

export default MaintenanceProposalsList
